'use client';

// This component must be present in a page's layout for useMfeLoadStore and any
// hook that depends on it (e.g. useAccountModalReady) to function correctly.
// See the JSDoc on useMfeLoadStore for current page coverage and guidance on
// extending it to additional layouts.

import { eventNames, useCoreEvents } from '@carvana/core-events';
import { useCallback } from 'react';
import { useMfeLoadStore } from '~/state/common/MfeLoadState';

const CORE_ACCOUNT_MODAL_MODULE = 'CoreAccountModal';

export default function MfeEventsListener() {
  const record = useMfeLoadStore(state => state.record);

  const handleMfeEvent = useCallback(
    (event: {
      componentName?: string;
      data?: string;
      eventName?: string;
      moduleName?: string;
    }) => {
      if (
        event.eventName === 'react-component' &&
        event.componentName &&
        event.data &&
        event.moduleName
      ) {
        record({
          componentName: event.componentName,
          data: event.data as 'error' | 'loaded' | 'timeout',
          moduleName: event.moduleName,
          timestamp: Date.now()
        });
      }
    },
    [record]
  );

  // MFER_EVENTS 'loaded' fires when the MFE bundle registers in globalThis — before its React
  // tree renders or initialises. ACCOUNT_MODAL_READY is the header MFE's application-level
  // signal that CoreAccountModal is fully initialised and safe to receive openAccountModal calls.
  // We record it as 'ready' so CTA components can gate on the correct semantic signal.
  // Pre-REO-8289: once the framework ships markReady/teardown props, MFEs will emit
  // MFER_EVENTS 'ready' directly and this listener can be removed.
  const handleAccountModalReady = useCallback(() => {
    record({
      componentName: 'default',
      data: 'ready',
      moduleName: CORE_ACCOUNT_MODAL_MODULE,
      timestamp: Date.now()
    });
  }, [record]);

  useCoreEvents(eventNames.mfer.events, handleMfeEvent);
  useCoreEvents(eventNames.accountModal.ready, handleAccountModalReady, {
    withHistory: true
  });

  return null;
}
